import { defineStore } from 'pinia'
import api from '@/services/api'
export interface EntityDefinitionSummary {
  id: string
  entityName: string
  displayName?: string
  schema?: string
  tableName?: string
}

export interface QueryResultData {
  columns: string[]
  rows: Record<string, any>[]
  singleValue?: boolean
  total?: number,
  totalElements?: number
  totalPages?: number
}

function normaliseQueryResponse(data: any): QueryResultData {
  if (Array.isArray(data?.columns) && Array.isArray(data?.rows)) {
    return { columns: data.columns, rows: data.rows, total: data.total, totalElements: data.totalElements, totalPages: data.totalPages }
  }
  if (Array.isArray(data?.content)) {
    const rows = data.content
    const columns = rows.length > 0 ? Object.keys(rows[0]) : []
    return { columns, rows, totalElements: data.totalElements, totalPages: data.totalPages }
  }
  if (Array.isArray(data) && data.length > 0) {
    return { columns: Object.keys(data[0]), rows: data }
  }
  if (Array.isArray(data?.data)) {
    const rows = data.data
    return { columns: rows.length > 0 ? Object.keys(rows[0]) : [], rows, total: data.total, totalElements: data.totalElements, totalPages: data.totalPages }
  }
  if (Array.isArray(data?.rows)) {
    const rows = data.rows
    return { columns: rows.length > 0 ? Object.keys(rows[0]) : [], rows, singleValue: data.singleValue, totalElements: data.totalElements, totalPages: data.totalPages }
  }
  return { columns: [], rows: [] }
}

function dataTypeIcon(dataType: string): string {
  const map: Record<string, string> = {
    STRING:   'ri-text',
    INTEGER:  'ri-hashtag',
    DECIMAL:  'ri-percent-line',
    BOOLEAN:  'ri-toggle-line',
    DATE:     'ri-calendar-line',
    UUID:     'ri-key-line',
    RELATION: 'ri-link-m',
    FILE:     'ri-file-line',
  }
  return map[dataType] ?? 'ri-question-line'
}

export const useScQueryBuilderStore = defineStore('sc-query-builder', {
  state: () => ({
    entities: [] as EntityDefinitionSummary[],
    entitiesLoading: false,
    queryResults: null as QueryResultData | null,
    queryLoading: false,
    queryError: null as string | null,
  }),

  actions: {
    async fetchEntities() {
      this.entitiesLoading = true
      try {
        const { data } = await api.get<EntityDefinitionSummary[]>('/entity')
        this.entities = data
      } catch (err) {
        console.error('fetchEntities:', err)
      } finally {
        this.entitiesLoading = false
      }
    },

    async fetchDeepFields(entityId: string): Promise<any[]> {
      try {
        const { data } = await api.get<any[]>(`/entity/${entityId}/fields/deep`)
        return data ?? []
      } catch (err: any) {
        if (err?.response?.status === 404) {
          return this._fetchFieldsFallback(entityId)
        }
        console.error('fetchDeepFields:', err)
        return []
      }
    },

    async _fetchFieldsFallback(entityId: string): Promise<any[]> {
      try {
        const { data } = await api.get<any[]>('/entity/fields', { params: { entityId } })
        return data
          .filter((f: any) => {
            if (f.dataType === 'RELATION' || f.dataType === 'FILE') return false
            if (f.dataType === 'UUID' && (f.relationEntityDefinition || f.relationEntity)) return false
            return true
          })
          .map((f: any) => ({
            path: [f.name],
            displayPath: [f.displayName],
            field: f.name,
            relation: null,
            relationChain: [],
            dataType: f.dataType,
            displayName: f.displayName,
            icon: dataTypeIcon(f.dataType),
          }))
      } catch (err) {
        console.error('fetchFieldsFallback:', err)
        return []
      }
    },

    async executeQuery(query: any) {
      this.queryLoading = true
      this.queryError = null
      try {
        const { data } = await api.post<any>('/entity-data/query', query)
        this.queryResults = normaliseQueryResponse(data)
      } catch (err: any) {
        console.error('executeQuery:', err)
        this.queryResults = null
        this.queryError = err?.message ?? 'Query failed'
      } finally {
        this.queryLoading = false
      }
    },

    async fetchSuggestions(
      entityName: string,
      fieldName: string,
      search: string,
    ): Promise<Array<{ value: string; label: string }>> {
      try {
        const { data } = await api.post<Array<string | { value: string; label: string }>>(
          '/entity-data/distinctValues',
          {
            entityName,
            fieldName,
            searchTerm: search,
            limit: 20,
          },
        )
        // Backend returns { value, label } pairs (translated label for translatable fields).
        // Tolerate the legacy raw-string shape during rollout.
        return (data ?? []).map((item) =>
          typeof item === 'string' ? { value: item, label: item } : item,
        )
      } catch {
        return []
      }
    },

    async exportCsv(query: any): Promise<string> {
      const { data } = await api.post<{ url: string }>('/entity-data/exportCSV', query)
      return data.url
    },
  },
})